import React, { useMemo, useState, useCallback, useRef } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { MOBILE_COLUMNS } from './columns.config';
import { formatDateCompact } from '@/lib/date_format';
import useLocalStorageState from '@/hooks/useLocalStorageState';
import MobileActualDateCell from './MobileActualDateCell';
import { useActualSaveState } from './useActualSaveState';
import { validateActualDates, todayISO } from './mobileActualValidation';
import { saveActualDate } from './save_actual_date';
import { apiErrorMessage } from '@/lib/apiError';

// Dedicated mobile (<=768px) Collaborator view — rendered by SchedulingScreen
// in place of the desktop split-pane (table + Gantt) when useIsMobile() is true.
// The desktop tree is left untouched, so desktop regression is structurally
// impossible. This view reuses the desktop's activity data, permission util
// (can(Action.EDIT_ACTUALS)) and save endpoint (saveActualDate ->
// PUT /activities/{id}/progress). See plan shimmering-munching-stallman.md.
//
// Config-driven 6-column view + search/filter. The four context columns are
// read-only; the two Actual columns (actualStart/actualFinish) render the shared
// <MobileActualDateCell> editor, gated by canEditActuals (= can(EDIT_ACTUALS)),
// and save through handleCommitActual -> saveActualDate (PUT .../progress). A
// Viewer sees the Actual columns as muted read-only text — same permission path.

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------
// "Needs actual" = work that has begun (status in_progress, OR its current
// start date is on/before today) but whose actual_start OR actual_finish is
// still missing, and is not already completed. This is the collaborator's
// to-do list: rows where someone should record what actually happened.
// There is no per-user assignment field on an activity, so we do NOT scope by
// assignee — "needs actual" is the closest defensible "your turn" signal.
const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'needs_actual', label: 'Needs actual' },
  { key: 'in_progress', label: 'In progress' },
];

function hasStarted(activity, today) {
  if (activity.status === 'in_progress') return true;
  const currentStart = activity.planned_start || activity.early_start;
  // Lexicographic compare is correct for zero-padded "YYYY-MM-DD".
  return Boolean(currentStart) && String(currentStart).slice(0, 10) <= today;
}

function needsActual(activity, today) {
  if (activity.status === 'completed') return false;
  if (!hasStarted(activity, today)) return false;
  return !activity.actual_start || !activity.actual_finish;
}

function matchesSearch(activity, query) {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const id = String(activity.activity_id || '').toLowerCase();
  const name = String(activity.name || '').toLowerCase();
  return id.includes(q) || name.includes(q);
}

// ---------------------------------------------------------------------------
// Card (one activity)
// ---------------------------------------------------------------------------
// The mobile schedule is a STACKED CARD per activity, NOT a 6-column table: on a
// phone a 6-col table pushed the editable Actual columns off-screen behind a
// horizontal scroll, hiding the superintendent's primary action. A card puts the
// ID + name in the header and the four dates in a 2x2 grid, so every field — and
// both editable Actual inputs — is visible with no horizontal scroll. Still
// driven by MOBILE_COLUMNS; the Actual cells reuse the shared
// <MobileActualDateCell> + the same permission gate (canEditActuals) + save path.
//
// Editability comes from the SAME permission the desktop uses
// (canEditActuals = can(Action.EDIT_ACTUALS)); a Viewer sees muted read-only
// text. Actual Finish is disabled until Actual Start exists. The cell NEVER calls
// the API — onCommit -> the parent's handleCommitActual (validate -> optimistic
// merge -> saveActualDate -> PUT /activities/{id}/progress). NB: we pass NO
// min/max to the native date input — a native picker silently clamps out-of-range
// picks to the bound (so max={data_date} once turned "2 Jul" into the data date
// with no feedback); validateActualDates enforces the bounds with a clear error.
const DATE_COLUMNS = MOBILE_COLUMNS.filter((c) => c.type === 'date');

function CardField({ label, children }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-tight text-slate-500">
        {label}
      </div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function ActivityCard({ activity, canEditActuals, saveState, errorMsgs, onCommit }) {
  return (
    <div
      data-testid="mobile-row"
      className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm"
    >
      {/* Header: ID chip + name (+ critical flag) */}
      <div className="flex items-start gap-2">
        <span className="shrink-0 font-mono text-xs text-slate-600 bg-slate-100 rounded px-1.5 py-0.5">
          {activity.activity_id}
        </span>
        <span className="flex-1 min-w-0 text-sm font-medium text-slate-900">
          {activity.name}
        </span>
        {activity.is_critical && (
          <span className="shrink-0 text-[10px] font-semibold text-red-600 bg-red-50 rounded px-1.5 py-0.5">
            Critical
          </span>
        )}
      </div>

      {/* The four dates: Current Start/Finish (read-only) + Actual Start/Finish
          (editable) in a 2x2 grid — all on-screen, no horizontal scroll. */}
      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-3">
        {DATE_COLUMNS.map((col) => (
          <CardField key={col.key} label={col.label}>
            {col.editable ? (
              <MobileActualDateCell
                value={col.accessor(activity)}
                editable={canEditActuals}
                disabled={col.field === 'actual_finish' && !activity.actual_start}
                label={`${col.label} — ${activity.activity_id || activity.name || 'activity'}`}
                saveState={saveState.getState(activity.id, col.field)}
                errorText={errorMsgs[`${activity.id}::${col.field}`] || null}
                onCommit={(v) => onCommit(activity, col.field, v)}
              />
            ) : (
              <span className="text-sm text-slate-600 tabular-nums">
                {formatDateCompact(col.accessor(activity))}
              </span>
            )}
          </CardField>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------
const MobileScheduleView = ({
  activities = [],
  canEditActuals = false,
  projectDataDate = null,
  activeProject = null,
  activeSchedule = null,
  onActivitiesMerged,
  onActivityChange,
}) => {
  const [search, setSearch] = useLocalStorageState('epicplan.mobile.search', '');
  const [filter, setFilter] = useLocalStorageState('epicplan.mobile.filter', 'all');
  const saveState = useActualSaveState();
  // Per-(activity,field) human-readable error message (the save-state hook tracks
  // only the enum). Keyed `${id}::${field}`.
  const [errorMsgs, setErrorMsgs] = useState({});
  // Monotonic sequence per (activity,field): only the LATEST in-flight save may
  // apply its outcome, so a stale save's late success/failure can't clobber a
  // newer save's value (concurrent-save race fix).
  const saveSeqRef = useRef(new Map());
  // Per-(activity,field) server-truth baseline + in-flight count: a failed save
  // reverts to the value the server actually holds, NOT a closure value that a
  // prior overlapping (and silently-failed) save left optimistically applied.
  const cellBaselineRef = useRef(new Map());
  const inFlightRef = useRef(new Map());
  const today = todayISO();

  // The single save path for an Actual edit: client-validate (mirrors the server
  // rules so the user sees the limit before a round-trip), optimistic-merge for
  // instant feedback, then persist via saveActualDate (the SAME endpoint +
  // server enforcement the desktop uses) and merge the CPM cascade; revert on
  // failure. A Phase-2 offline queue would wrap saveActualDate, not this.
  const handleCommitActual = useCallback(async (activity, field, value) => {
    const key = `${activity.id}::${field}`;
    // Claim the latest-save slot for this cell BEFORE validating: a new edit —
    // valid OR invalid — is the user's newest intent, so any in-flight save for
    // this cell is now stale and must not apply its outcome (e.g. a late 'saved'
    // overwriting the validation error this very edit raises).
    const mySeq = (saveSeqRef.current.get(key) || 0) + 1;
    saveSeqRef.current.set(key, mySeq);
    const isLatest = () => saveSeqRef.current.get(key) === mySeq;

    const actualStart = field === 'actual_start' ? value : activity.actual_start;
    const actualFinish = field === 'actual_finish' ? value : activity.actual_finish;
    const { ok, errors } = validateActualDates({ actualStart, actualFinish, dataDate: projectDataDate });
    const startKey = `${activity.id}::actual_start`;
    const finishKey = `${activity.id}::actual_finish`;
    if (!ok) {
      // Editing one actual can invalidate the OTHER (e.g. moving Start past an
      // existing Finish). Surface each field's error on its own cell.
      setErrorMsgs((prev) => ({ ...prev, [startKey]: errors.actualStart || null, [finishKey]: errors.actualFinish || null }));
      ['actual_start', 'actual_finish'].forEach((f) => {
        const e = f === 'actual_start' ? errors.actualStart : errors.actualFinish;
        if (e) saveState.markError(activity.id, f);
        else saveState.clear(activity.id, f);
      });
      return;
    }
    setErrorMsgs((prev) => {
      if (!(startKey in prev) && !(finishKey in prev)) return prev;
      const next = { ...prev };
      delete next[startKey];
      delete next[finishKey];
      return next;
    });
    saveState.markSaving(activity.id, field);
    // Snapshot the cell's server-truth baseline at the start of an edit session
    // (no save in flight); each accepted save updates it. This is what a failure
    // reverts to — correct even when overlapping saves on the same cell fail.
    const inflight = inFlightRef.current.get(key) || 0;
    if (inflight === 0) cellBaselineRef.current.set(key, activity[field] ?? null);
    inFlightRef.current.set(key, inflight + 1);

    // Optimistic: update ONLY the edited field (a partial merge), so a concurrent
    // edit to the other field or the server's CPM cascade isn't clobbered.
    if (onActivitiesMerged) onActivitiesMerged([{ id: activity.id, [field]: value }]);
    try {
      const { activity: updated, cascaded } = await saveActualDate(activity.id, field, value);
      // Server accepted this value — it is now this cell's server truth (update
      // the baseline even if this save is no longer the latest, so a later
      // failure of a newer save reverts to what the server really holds).
      cellBaselineRef.current.set(key, updated && (field in updated) ? (updated[field] ?? null) : value);
      if (!isLatest()) return; // superseded by a newer save — ignore this outcome
      if (onActivitiesMerged) onActivitiesMerged([updated, ...cascaded]);
      else if (onActivityChange) onActivityChange();
      saveState.markSaved(activity.id, field);
    } catch (e) {
      if (!isLatest()) return; // stale failure — don't clobber the newer save
      // Revert the edited field to the server-truth baseline (not a closure value).
      const baseline = cellBaselineRef.current.has(key)
        ? cellBaselineRef.current.get(key)
        : (activity[field] ?? null);
      if (onActivitiesMerged) onActivitiesMerged([{ id: activity.id, [field]: baseline ?? null }]);
      setErrorMsgs((prev) => ({ ...prev, [key]: apiErrorMessage(e, 'Save failed') }));
      saveState.markError(activity.id, field);
    } finally {
      const n = (inFlightRef.current.get(key) || 1) - 1;
      if (n <= 0) inFlightRef.current.delete(key);
      else inFlightRef.current.set(key, n);
    }
  }, [projectDataDate, onActivitiesMerged, onActivityChange, saveState]);

  const visible = useMemo(() => {
    const activeFilter = FILTERS.some((f) => f.key === filter) ? filter : 'all';
    return activities.filter((a) => {
      if (!matchesSearch(a, search)) return false;
      if (activeFilter === 'needs_actual') return needsActual(a, today);
      if (activeFilter === 'in_progress') return a.status === 'in_progress';
      return true;
    });
  }, [activities, search, filter, today]);

  const hasActivities = activities.length > 0;

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-white" data-testid="mobile-schedule-view">
      {/* Header */}
      <div className="px-4 pt-4 pb-2 border-b border-slate-200">
        <h1 className="text-base font-semibold text-slate-900 tracking-tight truncate">
          {activeProject?.name || 'Schedule'}
        </h1>
        {activeSchedule?.name && (
          <p className="text-xs text-slate-500 mt-0.5 truncate">{activeSchedule.name}</p>
        )}

        {/* Search */}
        <div className="relative mt-3">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          <Input
            type="text"
            inputMode="search"
            placeholder="Search by ID or name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-10"
            data-testid="mobile-search"
            aria-label="Search activities"
          />
        </div>

        {/* Filter segmented control */}
        <div
          className="mt-2 inline-flex w-full rounded-md border border-slate-200 bg-slate-50 p-0.5"
          role="tablist"
          aria-label="Filter activities"
        >
          {FILTERS.map((f) => {
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setFilter(f.key)}
                className={cn(
                  'flex-1 min-h-[36px] px-2 text-xs font-medium rounded transition-colors',
                  active
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                )}
                data-testid={`mobile-filter-${f.key}`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        {/* Results count */}
        <p className="mt-2 text-xs text-slate-500" data-testid="mobile-results-count">
          {visible.length} {visible.length === 1 ? 'activity' : 'activities'}
        </p>
      </div>

      {/* Body */}
      {!hasActivities ? (
        <div
          className="flex-1 flex items-center justify-center p-8 text-center"
          data-testid="mobile-empty"
        >
          <p className="text-sm text-slate-500">
            No activities in this schedule yet.
          </p>
        </div>
      ) : visible.length === 0 ? (
        <div
          className="flex-1 flex items-center justify-center p-8 text-center"
          data-testid="mobile-no-matches"
        >
          <p className="text-sm text-slate-500">No matching activities.</p>
        </div>
      ) : (
        // Stacked cards (NOT a horizontally-scrolling table): the page never
        // overflows sideways (overflow-x-hidden) and every field — including both
        // editable Actual inputs — is on-screen. Acceptance #9 + the off-screen
        // actuals fix.
        <div
          className="flex-1 overflow-y-auto overflow-x-hidden p-3 space-y-3 bg-slate-50"
          data-testid="mobile-card-list"
        >
          {visible.map((activity) => (
            <ActivityCard
              key={activity.id || activity.activity_id}
              activity={activity}
              canEditActuals={canEditActuals}
              saveState={saveState}
              errorMsgs={errorMsgs}
              onCommit={handleCommitActual}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MobileScheduleView;
